import { ListSubheader, List } from '@mui/material';
import { useLocation } from 'react-router-dom';
import SidebarMenuItem from './item';
import { defaultMenuItems, MenuItem, malaysiaMenuItems } from './items';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'src/app/hooks';

const MenuWrapper = styled(List)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(1)};
    padding: 0;

    & > .MuiList-root {
      padding: ${theme.spacing(1)} 0;
      .MuiListItem-root {
        padding-left: 8px;
      }
    }

    .MuiListSubheader-root {
      text-transform: uppercase;
      color: #F5F5F5;
      padding: ${theme.spacing(0.8, 1)};
      font-weight: 400;
      font-size: 12px;
      line-height: 130%;
    }
`
);

const renderSidebarMenuItems = ({
  items,
  path,
  search,
  isSnaDna,
  isPeoplePanel,
  isOfflineMedia,
  isCustomReport,
  isClustering,
  isOnlineMedia,
  isSocialMedia,
  isMainOverview,
  isGoogleReview,
  isEcommerce
}: {
  items: MenuItem[];
  path: string;
  search: string;
  isSnaDna: number;
  isPeoplePanel: number;
  isOfflineMedia: number;
  isCustomReport: number;
  isClustering: number;
  isOnlineMedia: number;
  isSocialMedia: number;
  isMainOverview: number;
  isGoogleReview: number;
  isEcommerce: number;
}): JSX.Element => {
  let tempItems = items;
  if (isSnaDna === 0) {
    tempItems = tempItems.filter(
      (item) => !(item.name === 'SNA' || item.name === 'DNA')
    );
  }
  if (isPeoplePanel === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'People Panel'));
  }
  if (isOfflineMedia === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Others Media'));
  }
  if (isCustomReport === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Report'));
  }
  if (isClustering === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Clustering'));
  }
  if (isSocialMedia === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Social Media'));
  }
  if (isOnlineMedia === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Online Media'));
  }
  if (isMainOverview === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Main Overview'));
  }
  if (isGoogleReview === 0) {
    tempItems = tempItems.filter((item) => !(item.name === 'Google Review'));
  }
  if (!isEcommerce) {
    tempItems = tempItems.filter((item) => !(item.name === 'E-Commerce'));
  }
  return (
    <List>
      {tempItems.reduce(
        (ev, item) =>
          reduceChildRoutes({
            ev,
            item,
            path,
            search,
            isSnaDna,
            isPeoplePanel,
            isOfflineMedia,
            isCustomReport,
            isClustering,
            isOnlineMedia,
            isSocialMedia,
            isMainOverview,
            isGoogleReview,
            isEcommerce
          }),
        []
      )}
    </List>
  );
};

const reduceChildRoutes = ({
  ev,
  path,
  search,
  item,
  isSnaDna,
  isPeoplePanel,
  isOfflineMedia,
  isCustomReport,
  isClustering,
  isOnlineMedia,
  isSocialMedia,
  isMainOverview,
  isGoogleReview,
  isEcommerce
}: {
  ev: JSX.Element[];
  path: string;
  search: string;
  item: MenuItem;
  isSnaDna: number;
  isPeoplePanel: number;
  isOfflineMedia: number;
  isCustomReport: number;
  isClustering: number;
  isOnlineMedia: number;
  isSocialMedia: number;
  isMainOverview: number;
  isGoogleReview: number;
  isEcommerce: number;
}): Array<JSX.Element> => {
  const key = item.name;

  const currentPage = path + search;
  const exactMatch =
    path === '/overview' || path === '/social_media/overview'
      ? currentPage === item.link
      : currentPage.includes(item.link);

  if (item.items) {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        open={exactMatch}
        name={item.name}
        icon={item.icon}
        link={item.link}
        badge={item.badge}
      >
        {renderSidebarMenuItems({
          path,
          search,
          items: item.items,
          isSnaDna: isSnaDna,
          isPeoplePanel: isPeoplePanel,
          isOfflineMedia: isOfflineMedia,
          isCustomReport: isCustomReport,
          isClustering: isClustering,
          isOnlineMedia: isOnlineMedia,
          isSocialMedia: isSocialMedia,
          isMainOverview: isMainOverview,
          isGoogleReview: isGoogleReview,
          isEcommerce: isEcommerce
        })}
      </SidebarMenuItem>
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        name={item.name}
        link={item.link}
        badge={item.badge}
        icon={item.icon}
      />
    );
  }

  return ev;
};

function SidebarMenu() {
  const location = useLocation();
  const {
    isSnaDna,
    isPeoplePanel,
    isOfflineMedia,
    isCustomReport,
    isClustering,
    isOnlineMedia,
    isSocialMedia,
    isMainOverview,
    isGoogleReview,
    isEcommerce
  } = useAppSelector((state) => state.users.project.flags);

  const { timezone } = useAppSelector((state) => state.users.project);
  const menuItems = timezone === 7 ? defaultMenuItems : malaysiaMenuItems;

  return (
    <>
      {menuItems.map((section) => (
        <MenuWrapper
          key={section.heading}
          subheader={
            section.heading ? (
              <ListSubheader component="div" disableSticky>
                {section.heading}
              </ListSubheader>
            ) : undefined
          }
        >
          {renderSidebarMenuItems({
            items: section.items,
            path: location.pathname,
            search: location.search,
            isSnaDna,
            isPeoplePanel,
            isOfflineMedia,
            isCustomReport,
            isClustering,
            isOnlineMedia,
            isSocialMedia,
            isMainOverview,
            isGoogleReview,
            isEcommerce
          })}
        </MenuWrapper>
      ))}
    </>
  );
}

export default SidebarMenu;
